import { motion, useInView } from "motion/react";
import { Send, CheckCircle, Loader2 } from "lucide-react";
import { useRef, useState, useEffect } from "react";
import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile";
import { useLanguage } from "../../i18n/LanguageContext";
import { translations } from "../../i18n/translations";

export function ContactForm() {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileInstance>(null);

  const sectionRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0 });
  const [fallback, setFallback] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setFallback(true), 2000);
    return () => clearTimeout(t);
  }, []);

  const shouldAnimate = isInView || fallback;

  const { language } = useLanguage();
  const t = translations[language].contact;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!turnstileToken) return;
    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          turnstile_token: turnstileToken,
        }),
      });

      if (!res.ok) throw new Error();

      setSubmitted(true);
      setFormData({ name: "", phone: "" });
      setTimeout(() => setSubmitted(false), 3000);
    } catch {
      setError(t.errorMessage);
    } finally {
      setLoading(false);
      setTurnstileToken(null);
      turnstileRef.current?.reset();
    }
  };

  return (
    <section
      id="kontakt"
      className="py-28 px-6"
      style={{
        background: "var(--surface-1)",
        borderTop: "1px solid var(--border)",
      }}
    >
      <div ref={sectionRef} className="max-w-4xl mx-auto">
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={shouldAnimate ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
        >
          <h2
            className="text-4xl md:text-5xl mb-4"
            style={{ color: "var(--foreground)", fontFamily: "var(--font-display)" }}
          >
            {t.heading}{" "}
            <em style={{ color: "var(--gold)", fontStyle: "italic" }}>{t.headingAccent}</em>?
          </h2>
          <p
            className="text-lg max-w-2xl mx-auto"
            style={{ color: "var(--foreground-muted)", fontFamily: "var(--font-body)" }}
          >
            {t.subheading}
          </p>
        </motion.div>

        <motion.div
          className="rounded-3xl p-8 md:p-12 relative overflow-hidden"
          style={{
            background: "var(--surface-2)",
            border: "1px solid var(--border)",
          }}
          initial={{ opacity: 0, y: 50 }}
          animate={shouldAnimate ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {/* Ambient glow in top-right corner */}
          <div
            className="absolute -top-24 -right-24 w-64 h-64 rounded-full blur-3xl pointer-events-none"
            style={{ background: "var(--glow-gold)", opacity: 0.5 }}
          />

          <div className="relative z-10">
            {submitted ? (
              <motion.div
                className="text-center py-12"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              >
                <div
                  className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center"
                  style={{
                    background: "rgba(212, 149, 106, 0.12)",
                    border: "1px solid var(--border-strong)",
                  }}
                >
                  <CheckCircle className="w-8 h-8" style={{ color: "var(--gold)" }} strokeWidth={1.5} />
                </div>
                <h3
                  className="text-2xl mb-2"
                  style={{ fontFamily: "var(--font-display)", color: "var(--foreground)" }}
                >
                  {t.successHeading}
                </h3>
                <p style={{ color: "var(--foreground-muted)", fontFamily: "var(--font-body)" }}>
                  {t.successBody}
                </p>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm mb-2"
                    style={{ color: "var(--cream-dim)", fontFamily: "var(--font-body)" }}
                  >
                    {t.labelName}
                  </label>
                  <input
                    type="text"
                    id="name"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl outline-none transition-all"
                    style={{
                      background: "var(--surface-3)",
                      border: "1px solid var(--border)",
                      color: "var(--foreground)",
                      fontFamily: "var(--font-body)",
                    }}
                    placeholder={t.placeholderName}
                    onFocus={(e) => {
                      e.target.style.borderColor = "var(--border-glow)";
                      e.target.style.boxShadow = "0 0 0 3px rgba(212, 149, 106, 0.12)";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "var(--border)";
                      e.target.style.boxShadow = "none";
                    }}
                  />
                </div>

                <div>
                  <label
                    htmlFor="phone"
                    className="block text-sm mb-2"
                    style={{ color: "var(--cream-dim)", fontFamily: "var(--font-body)" }}
                  >
                    {t.labelPhone}
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl outline-none transition-all"
                    style={{
                      background: "var(--surface-3)",
                      border: "1px solid var(--border)",
                      color: "var(--foreground)",
                      fontFamily: "var(--font-body)",
                    }}
                    placeholder={t.placeholderPhone}
                    onFocus={(e) => {
                      e.target.style.borderColor = "var(--border-glow)";
                      e.target.style.boxShadow = "0 0 0 3px rgba(212, 149, 106, 0.12)";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "var(--border)";
                      e.target.style.boxShadow = "none";
                    }}
                  />
                </div>

                {isInView && (
                  <Turnstile
                    ref={turnstileRef}
                    siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY}
                    onSuccess={(token) => setTurnstileToken(token)}
                    onExpire={() => setTurnstileToken(null)}
                    onError={() => setTurnstileToken(null)}
                    className="hidden"
                    options={{ theme: "dark" }}
                  />
                )}

                {error && (
                  <p className="text-sm" style={{ color: "#E57373", fontFamily: "var(--font-body)" }}>
                    {error}
                  </p>
                )}

                <motion.button
                  type="submit"
                  disabled={!turnstileToken || loading}
                  className="w-full py-4 px-6 rounded-xl flex items-center justify-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    background: "linear-gradient(135deg, var(--gold) 0%, var(--gold-bright) 50%, var(--gold) 100%)",
                    backgroundSize: "200% 100%",
                    color: "#0A0A0B",
                    fontFamily: "var(--font-body)",
                    fontWeight: 600,
                    boxShadow: "0 0 40px var(--glow-gold-strong), 0 4px 24px rgba(0,0,0,0.4)",
                  }}
                  whileHover={
                    turnstileToken && !loading
                      ? { scale: 1.02, boxShadow: "0 0 60px var(--glow-gold-strong), 0 8px 32px rgba(0,0,0,0.5)" }
                      : {}
                  }
                  whileTap={!loading ? { scale: 0.98 } : {}}
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <motion.span
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        style={{ display: "inline-flex" }}
                      >
                        <Loader2 className="w-4 h-4" />
                      </motion.span>
                      {t.submitLoading}
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      {t.submitIdle}
                      <Send className="w-4 h-4" />
                    </span>
                  )}
                </motion.button>
              </form>
            )}
          </div>
        </motion.div>

        <motion.p
          className="text-center text-sm mt-8"
          style={{ color: "var(--foreground-muted)", fontFamily: "var(--font-body)" }}
          initial={{ opacity: 0 }}
          animate={shouldAnimate ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          {t.footnote}
        </motion.p>
      </div>
    </section>
  );
}
